/* eslint-disable camelcase */
import React, { useEffect, useState } from "react";
import Types from "prop-types";
import queryString from "query-string";
import i18n from "i18next";
import { Trans } from "react-i18next";

import {
  loginWithOneClick,
  proofApproveRequest,
} from "../../gateway/LoginGateway";
import OneClickPhoneLoginForm from "../forms/OneClickPhoneLoginForm";
import { sanitizePhone, maskPhoneNumber } from "../../common/PhoneNumber";
import { ARGUS_APM } from "../../../apm";
import { fetchGuardToken } from "../../Guard";
import PushNotificationView from "./PushNotificationView";
import OneClickErrorView from "./OneClickErrorView";
import RedirectToLogin, {
  CSRF_SESSION_STORAGE_FIELD,
} from "../../common/RedirectToLogin";
import Analytika from "../../../analytika";
import { isSessionStorage, parseAcrValues } from "../../../utils/helpers";
import {
  ONE_CLICK_PHONE_LOGIN_FORM_VIEW,
  REDIRECT_TO_IDP,
  REDIRECT_TO_SIGNUP,
  PUSH_NOTIFICATION,
  UNREGISTERED_USER,
  REJECTED_USER,
  MAX_TRIES_REACHED,
} from "./constants";

function OneClickView(props) {
  const {
    location: { search },
  } = props;
  const { csrf, acr_values: acrValues } = queryString.parse(search);
  const externalParams = parseAcrValues(acrValues);

  const [state, setState] = useState({
    submitting: false,
    view: ONE_CLICK_PHONE_LOGIN_FORM_VIEW,
    redirectToPush: null,
    clientId: null,
    rememberMe: true,
    error: null,
  });

  useEffect(() => {
    Analytika.fireEvent("phone_screen_opened");
  }, []);

  useEffect(() => {
    Analytika.setSessionAttr(externalParams);
  }, [externalParams]);

  useEffect(() => {
    if (isSessionStorage() && csrf) {
      sessionStorage.setItem(CSRF_SESSION_STORAGE_FIELD, csrf);
    }
  }, [csrf]);

  const onPhoneChange = (phone) => {
    if (phone.isValid) {
      setState((prevState) => ({ ...prevState, phone }));
    }
    Analytika.setSessionAttr({
      phoneNumber: sanitizePhone(phone.fullPhoneNumber),
    });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    Analytika.fireEvent("continue_button_clicked");
    if (state.phone) {
      setState((prevState) => ({
        ...prevState,
        submitting: true,
        error: null,
      }));
      handleLoginResponse(doLogin());
    } else {
      setState((prevState) => ({
        ...prevState,
        submitting: false,
        error: "Please enter correct phone number.",
      }));
    }
  };

  const handleErrors = (error) => {
    setState((prevState) => ({
      ...prevState,
      submitting: false,
      error: error.response?.data?.message || "no response",
    }));
  };

  const handleUnknown400Errors = (error) => {
    let errorMessage = i18n.t("NO_RESPONSE");
    if (error?.additional_information?.error_code) {
      if (error?.additional_information?.error_code === "USER_BLOCKED") {
        errorMessage = (
          <Trans i18nKey="spring_security_error:USER_BLOCKED" t={props.t}>
            We cannot log you in at this time.
            <a href="https://help.careem.com/hc/en-us/requests/new?reason=account_issue">
              Please contact us for help.
            </a>
          </Trans>
        );
      } else {
        errorMessage = i18n.t(
          "spring_security_error:" + error?.additional_information?.error_code
        );
      }
    } else if (error?.errorCode) {
      errorMessage = i18n.t("spring_security_error:" + error?.errorCode);
    } else if (error?.error_message) {
      errorMessage = error?.error_message;
    } else if (error?.errorMessage) {
      errorMessage = error?.errorMessage;
    }
    setState((prevState) => ({
      ...prevState,
      submitting: false,
      error: errorMessage,
    }));
  };

  const handleLoginResponse = (promise) => {
    promise
      .then((data) => {
        return redirectToPush(data);
      })
      .catch((error) => {
        if (ARGUS_APM) {
          ARGUS_APM.captureError(error);
        }
        if (error.response?.status === 400) {
          handle400Errors(error);
        } else {
          handleErrors(error);
        }
      });
  };

  const doLogin = async (overrides) => {
    const params = Object.assign(
      {
        phone_number: sanitizePhone(state.phone.fullPhoneNumber),
        guard: await fetchGuardToken(),
        is_retry: false,
        external_params: externalParams,
      },
      overrides
    );
    return loginWithOneClick(params, state.clientId);
  };

  const handle400Errors = (error) => {
    switch (error.response.data?.error) {
      case UNREGISTERED_USER:
        setState((prevState) => ({
          ...prevState,
          submitting: false,
          view: REDIRECT_TO_SIGNUP,
        }));
        break;
      default:
        handleUnknown400Errors(error.response.data);
        break;
    }
  };

  const redirectToPush = (response) => {
    setState((prevState) => ({
      ...prevState,
      redirectToPush: response,
      view: PUSH_NOTIFICATION,
    }));
  };

  const onConfirm = (id) => {
    proofApproveRequest(id, state.clientId)
      .then(({ token }) => {
        setState((prevState) => ({
          ...prevState,
          redirectToIdpParams: {
            fieldName: "proofToken",
            value: token,
          },
          view: REDIRECT_TO_IDP,
        }));
      })
      .catch(() => {
        setState((prevState) => ({
          ...prevState,
          view: REJECTED_USER,
        }));
      });
  };

  const onReject = (error = "") => {
    if (error === "maxTriesReached") {
      setState((prevState) => ({
        ...prevState,
        view: MAX_TRIES_REACHED,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        view: REJECTED_USER,
      }));
    }
  };

  const onCheckboxChange = (event) => {
    const { checked } = event.target;
    setState((prevState) => ({
      ...prevState,
      rememberMe: checked,
    }));
  };

  const { error, submitting, view } = state;
  const merchant = externalParams?.["merchant"] || "Unknown";

  switch (view) {
    case ONE_CLICK_PHONE_LOGIN_FORM_VIEW:
      return (
        <OneClickPhoneLoginForm
          error={error}
          merchant={merchant}
          rememberMe={state.rememberMe}
          submitting={submitting}
          onPhoneChange={onPhoneChange}
          onCheckboxChange={onCheckboxChange}
          onSubmit={onSubmit}
        />
      );
    case PUSH_NOTIFICATION:
      return (
        <PushNotificationView
          token={state.redirectToPush}
          phoneNumber={state.phone.fullPhoneNumber}
          merchant={merchant}
          clientId={state.clientId}
          doLogin={doLogin}
          onConfirm={onConfirm}
          onReject={onReject}
        />
      );
    case REDIRECT_TO_SIGNUP:
      return (
        <OneClickErrorView
          heading={"We couldn’t verify you"}
          subHeading={maskPhoneNumber(state.phone.fullPhoneNumber)}
          description={"Please sign up on Careem app and try again."}
          showUpdateButton={true}
        />
      );
    case REDIRECT_TO_IDP:
      return (
        <RedirectToLogin
          fieldName={state.redirectToIdpParams.fieldName}
          value={state.redirectToIdpParams.value}
          rememberMe={state.rememberMe}
          csrf={
            csrf ||
            (isSessionStorage() &&
              sessionStorage.getItem(CSRF_SESSION_STORAGE_FIELD))
          }
        />
      );
    case REJECTED_USER:
      return (
        <OneClickErrorView
          heading={"We couldn’t verify you"}
          subHeading={maskPhoneNumber(state.phone.fullPhoneNumber)}
          description={
            "Please update your Careem app and try again if you don’t have the latest version of the app."
          }
          showUpdateButton={true}
        />
      );
    case MAX_TRIES_REACHED:
      return (
        <OneClickErrorView
          heading={"Maximum retries reached"}
          subHeading={maskPhoneNumber(state.phone.fullPhoneNumber)}
          description={
            "Please update your Careem app and try again if you don’t have the latest version of the app."
          }
          showUpdateButton={true}
        />
      );
  }
}

OneClickView.propTypes = {
  t: Types.func,
  location: Types.object.isRequired,
  history: Types.object.isRequired,
};

export default OneClickView;
